import { useEffect, useMemo, useRef, useDeferredValue } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import { useCheckHoliday } from '@/hooks';
import {
  calculateTradeValues,
  TradeEntryFormData,
} from '@/services/trades/trade-service';
import { positionToFormValues } from '@/services/trades/amend-mapper';
import { ALL_TRADE_TYPES } from '@/types';
import { pricedByPool } from '@/utils/trades/cash-pool-rate';
import type { PositionDetail, BenchmarkRate, TradeType } from '@/types';
import type { TradeFormValues } from './useCreateTradeForm';
import type { TradeForm, CalculatedValues } from '../components/trade-form-sections/types';
import { TRADE_TYPE_LABELS } from '../constants/trade-labels';
import { useTradeFormReferenceData } from './useTradeFormReferenceData';

/**
 * LR-484 amend controller — the C-lite counterpart to useCreateTradeForm.
 *
 * It REUSES the same trade-form section components and the same preview math
 * (calculateTradeValues), but is deliberately THIN: it seeds the form from a booked
 * PositionDetail and runs NONE of create's inventory-prefill / benchmark-sync /
 * rate-reset / custodian-compute effects (which would clobber the booked values).
 * Editability is governed by the sections' `amendMode` prop + the AMEND_FIELDS
 * registry (isAmendLocked); LR-495 removed the synthetic `selectedInventoryItem` the
 * sections used to gate on — they now take `securityDisplay` / `securityCurrency`
 * for read-only display instead. useCreateTradeForm / CreateTradeModal are untouched.
 */
export function useAmendTradeForm({ trade, isOpen }: { trade: PositionDetail; isOpen: boolean }) {
  // Registries only feed the (locked) display selects; gate fetches on open.
  const { isUsingFallbackRates, counterparties, custodians, benchmarkRates, benchmarkMeta } =
    useTradeFormReferenceData(isOpen);

  const form = useForm({ defaultValues: useMemo(() => positionToFormValues(trade), [trade]) });

  // Reseed strictly on the closed->open transition (ref edge-guard), mirroring the
  // old AmendModal: a background trade refetch while OPEN must not clobber edits.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      form.reset(positionToFormValues(trade));
    }
    wasOpen.current = isOpen;
  }, [isOpen, trade, form]);

  // LR-495: amend has a booked Position, not an inventory pick. Instead of
  // fabricating a synthetic SearchInventoryItem (removed), expose just what the
  // reused sections actually need: the booked security identity (read-only display)
  // and the booked available quantity (preview math). Section editability is driven
  // by amendMode + the registry (isAmendLocked), no longer by a selectedInventoryItem.
  const securityDisplay = useMemo(
    () => ({
      symbolId: trade.symbol_id,
      shortName: trade.description || trade.symbol_id,
      sedol: trade.sedol ?? '',
      isin: trade.isin ?? '',
    }),
    [trade],
  );
  const availableQuantity = trade.available_quantity ?? trade.quantity ?? 0;

  // Field reads (mirror useCreateTradeForm).
  const quantity = useStore(form.store, (s: { values: TradeFormValues }) => s.values.quantity);
  const grossPrice = useStore(form.store, (s: { values: TradeFormValues }) => s.values.gross_price);
  const marginBps = useStore(form.store, (s: { values: TradeFormValues }) => s.values.margin_bps);
  const benchmarkRate = useStore(form.store, (s: { values: TradeFormValues }) => s.values.benchmark_rate);
  const rateType = useStore(form.store, (s: { values: TradeFormValues }) => s.values.rate_type);
  const rateEntryMode = useStore(form.store, (s: { values: TradeFormValues }) => s.values.rate_entry_mode);
  const benchmarkName = useStore(form.store, (s: { values: TradeFormValues }) => s.values.benchmark_name);
  const feePercent = useStore(form.store, (s: { values: TradeFormValues }) => s.values.fee_percent);
  const spreadBps = useStore(form.store, (s: { values: TradeFormValues }) => s.values.spread_bps);
  const dayCountConvention = useStore(form.store, (s: { values: TradeFormValues }) => s.values.day_count_convention);
  const tradeDate = useStore(form.store, (s: { values: TradeFormValues }) => s.values.trade_date);
  const settlementDate = useStore(form.store, (s: { values: TradeFormValues }) => s.values.settlement_date);
  const maturityDate = useStore(form.store, (s: { values: TradeFormValues }) => s.values.maturity_date);
  const tradeDuration = useStore(form.store, (s: { values: TradeFormValues }) => s.values.trade_duration);
  const noticePeriodDays = useStore(form.store, (s: { values: TradeFormValues }) => s.values.notice_period_days);
  const settlementCurrency = useStore(form.store, (s: { values: TradeFormValues }) => s.values.settlement_currency);
  const fxRate = useStore(form.store, (s: { values: TradeFormValues }) => s.values.fx_rate);
  const netSettlementConvertedInput = useStore(form.store, (s: { values: TradeFormValues }) => s.values.net_settlement_converted_input);
  const tradeType = useStore(form.store, (s: { values: TradeFormValues }) => s.values.trade_type);
  const counterpartyName = useStore(form.store, (s: { values: TradeFormValues }) => s.values.counterparty_name);
  const settlementVenue = useStore(form.store, (s: { values: TradeFormValues }) => s.values.settlement_venue);
  const deliveryType = useStore(form.store, (s: { values: TradeFormValues }) => s.values.delivery_type);
  const collateralType = useStore(form.store, (s: { values: TradeFormValues }) => s.values.collateral_type);
  const collateralQuantity = useStore(form.store, (s: { values: TradeFormValues }) => s.values.collateral_quantity);
  const collateralCurrentMarketPrice = useStore(form.store, (s: { values: TradeFormValues }) => s.values.collateral_current_market_price);
  const collateralPercent = useStore(form.store, (s: { values: TradeFormValues }) => s.values.collateral_percent);

  const { data: tradeDateHoliday } = useCheckHoliday(tradeDate, !!tradeDate);
  const { data: settlementDateHoliday } = useCheckHoliday(settlementDate, !!settlementDate);

  const deferredQuantity = useDeferredValue(quantity);
  const deferredGrossPrice = useDeferredValue(grossPrice);
  const deferredMarginBps = useDeferredValue(marginBps);
  const deferredBenchmarkRate = useDeferredValue(benchmarkRate);
  const deferredFeePercent = useDeferredValue(feePercent);
  const deferredSpreadBps = useDeferredValue(spreadBps);

  // totalRate — identical sign convention to useCreateTradeForm (LR-464).
  const totalRate = useMemo(() => {
    const benchmark = typeof deferredBenchmarkRate === 'string' ? (parseFloat(deferredBenchmarkRate) || 0) : (deferredBenchmarkRate || 0);
    if (rateEntryMode === 'fee') {
      const fee = typeof deferredFeePercent === 'string' ? parseFloat(deferredFeePercent) : (deferredFeePercent || 0);
      return fee - benchmark;
    }
    if (rateType === 'fixed') return benchmark;
    const spread = typeof deferredSpreadBps === 'string' ? parseFloat(deferredSpreadBps) : (deferredSpreadBps || 0);
    return benchmark + spread / 100;
  }, [rateEntryMode, rateType, deferredBenchmarkRate, deferredFeePercent, deferredSpreadBps]);

  // LR-631: undefined unless a pool actually prices this trade, which is what
  // `calculateTradeValues` keys the pool branch on. `pricedByPool` and not the
  // bare pool + mode pair, so the preview divides by the pool's basis on exactly
  // the trades the backend does (dp review, Low 2).
  const poolRate = pricedByPool(trade)
    ? (Number(trade.effective_benchmark_rate ?? trade.benchmark_rate) || undefined)
    : undefined;
  const poolDayCount = poolRate !== undefined
    ? (Number(trade.cash_pool_day_count) || undefined)
    : undefined;

  const securityCurrency = trade.currency || 'USD';
  const effectiveSettlementCurrency = settlementCurrency || securityCurrency;
  const isCrossCurrency = effectiveSettlementCurrency !== securityCurrency;
  const isCashMatchingMode = !!netSettlementConvertedInput && netSettlementConvertedInput > 0;

  // Live preview — same flat->nested adapter + calculateTradeValues as create.
  const calculatedValues = useMemo(() => {
    const parsedGrossPrice = typeof deferredGrossPrice === 'string' ? parseFloat(deferredGrossPrice) : deferredGrossPrice;
    const calculationData: Partial<TradeEntryFormData> = {
      security: { currency: securityCurrency },
      position: {
        quantity: deferredQuantity || 0,
        contractPrice: parsedGrossPrice || 0,
        marginBps: deferredMarginBps || 0,
        availableQuantity,
        tradeType: tradeType as TradeType | undefined,
      },
      rate: {
        fixedRate: totalRate,
        rateEntryMode,
        benchmark: benchmarkName,
        benchmarkRate: benchmarkRates[benchmarkName as BenchmarkRate] ?? 0,
        spreadBps: typeof deferredSpreadBps === 'string' ? parseFloat(deferredSpreadBps) || 0 : (deferredSpreadBps || 0),
        feePercent: typeof deferredFeePercent === 'string' ? parseFloat(deferredFeePercent) || 0 : (deferredFeePercent || 0),
        dayCountConvention: dayCountConvention ?? undefined,
        // LR-631: a pooled fee trade's cost leg prices on its POOL's rate and
        // basis. This form runs none of create's registry effects and the pool
        // is not amendable, so both come off the booked trade rather than from a
        // query: today's pool rate where the read path serves one, the booking
        // snapshot otherwise. Without them the renamed "Cash Pool Interest
        // Accrual" inside the approval screen would be `benchmarkRates[name]` -
        // the bare index, missing the pool's spread, on the trade's day count,
        // and a flat 0 for a fixed pool (which books under benchmark `other`,
        // absent from the registry).
        poolRate,
        poolDayCount,
      },
      dates: {
        tradeDate,
        settlementDate,
        tradeDuration: tradeDuration as 'OPEN' | 'EVERGREEN' | 'TERM' | undefined,
        maturityDate: maturityDate ?? undefined,
        noticePeriodDays: noticePeriodDays ?? undefined,
      },
      settlement: {
        settlementCurrency: effectiveSettlementCurrency,
        fxRate,
        netSettlementConvertedInput,
      },
    };
    const base = calculateTradeValues(calculationData);
    return {
      ...base,
      netSettlementSecurity: base.netSettlement,
      collateralRequiredSecurity: base.collateralRequired,
      expectedDailyReturnSecurity: base.expectedReturn,
      marginPriceSettlement: base.marginPriceConverted,
      expectedDailyReturnSettlement: base.expectedDailyReturnConverted,
      contractValueSettlement: base.contractValueConverted,
    };
  }, [
    deferredQuantity, deferredGrossPrice, deferredMarginBps, maturityDate, tradeDate,
    settlementDate, tradeDuration, noticePeriodDays, availableQuantity,
    securityCurrency, effectiveSettlementCurrency, fxRate, netSettlementConvertedInput,
    totalRate, rateEntryMode, benchmarkName, benchmarkRates, deferredSpreadBps,
    deferredFeePercent, tradeType, dayCountConvention, poolRate, poolDayCount,
  ]);

  // Conditions are locked in amend, but availableTradeTypes still feeds the (disabled)
  // trade-type select so it renders the right option set. LR-634: all four legs,
  // matching Trade Entry - the counterparty registry narrows nothing.
  const availableTradeTypes = ALL_TRADE_TYPES;

  // The PATCH body (diffAmendPayload over the live form values) is computed by the
  // single consumer — AmendTradeModal — directly off `values`. This hook intentionally
  // does NOT re-expose it (kvi review #3: no parallel payload-diff source to drift).

  return {
    form: form as unknown as TradeForm,
    securityDisplay,
    counterparties,
    custodians,
    benchmarkRates,
    benchmarkMeta,
    calculatedValues: calculatedValues as unknown as CalculatedValues,
    totalRate,
    availableTradeTypes,
    tradeTypeLabels: TRADE_TYPE_LABELS,
    rateType,
    rateEntryMode,
    tradeDuration,
    settlementDate,
    counterpartyName,
    deliveryType,
    settlementVenue,
    collateralType,
    collateralQuantity,
    collateralCurrentMarketPrice,
    collateralPercent,
    fxRate,
    securityCurrency,
    effectiveSettlementCurrency,
    isCrossCurrency,
    isCashMatchingMode,
    isUsingFallbackRates,
    isFormDisabled: false,
    tradeDateHoliday,
    settlementDateHoliday,
  };
}
